// src/components/modals/GlobalQuickModals.jsx
import React, { useCallback } from "react";
import { useQuickModal } from "../../hooks/QuickModalContext";
import { useStatus } from "../../popup/NotificationContext";

// ✅ Modals
import AddTripModal from "../modals/AddTripModal";
import RoutePortalModal from "../modals/RoutePortalModal";
import LicensePortalModal from "../modals/LicensePortalModal";
import ExpensePortalModal from "../modals/ExpensePortalModal";
import UserPortalModal from "../modals/UserPortalModal";
import DealerPortalModal from "../modals/DealerPortalModal";
import SubAdminPortalModal from "../modals/SubAdminPortalModal";
import EmployeePortalModal from "../modals/EmployeePortalModal";
import DeviceListPortalModal from "../modals/DeviceListPortalModal";
import DriverPortalModal from "../modals/DriverPortalModal";
import AddDocumentModal from "../modals/AddDocumentModal";
import AddMaterialModal from "../modals/AddMaterialModal";
import AddClientModal from "../modals/AddClientModal";
import ReminderPortalModal from "../modals/ReminderPortalModal";
import SensorPortalModal from "../modals/SensorPortalModal";
import DeviceModelModal from "../modals/device_model_modal";
import ReportsPortal from "../reports/ReportsPortal";

// ✅ API Hooks (same as AdvancedMapTracker)
import {
  useAddTripMutation,
  useAddDocumentMutation,
  useAddMaterialMutation,
  useAddClientMutation,
} from "../../redux/logistics/logisticsApi";

export default function GlobalQuickModals() {
  const { openType, closeModal, payload } = useQuickModal();
  const { showStatus } = useStatus();

  const [addTrip] = useAddTripMutation();
  const [addDocument] = useAddDocumentMutation();
  const [addMaterial] = useAddMaterialMutation();
  const [addClient] = useAddClientMutation();

  const handleTripSubmit = useCallback(async (data) => {
    try {
      await addTrip(data).unwrap();
      showStatus("success", "Trip saved.");
      closeModal();
    } catch (err) {
      console.error(err);
      showStatus("error", err?.data?.error || "Failed to save trip.");
    }
  }, [addTrip, closeModal, showStatus]);

  const handleClientSubmit = useCallback(async (data) => {
    try {
      await addClient(data).unwrap();
      showStatus("success", "Client added successfully");
      closeModal();
    } catch (err) {
      console.error(err);
      showStatus("error", err?.data?.error || "Failed to add client");
    }
  }, [addClient, closeModal, showStatus]);

  const handleMaterialSubmit = useCallback(async (data) => {
    try {
      await addMaterial(data).unwrap();
      showStatus("success", "Material added.");
      closeModal();
    } catch (err) {
      console.error(err);
      showStatus("error", err?.data?.error || "Failed to add material.");
    }
  }, [addMaterial, closeModal, showStatus]);

  const handleDocumentSubmit = useCallback(async (data) => {
    try {
      const fd = new FormData();
      Object.keys(data).forEach((k) => data[k] && fd.append(k, data[k]));
      await addDocument(fd).unwrap();
      showStatus("success", "Document uploaded.");
      closeModal();
    } catch (err) {
      console.error(err);
      showStatus("error", err?.data?.error || "Failed to upload document.");
    }
  }, [addDocument, closeModal, showStatus]);

  if (!openType) return null;

  return (
    <>
      {openType === "trip" && (
        <AddTripModal onClose={closeModal} onSubmit={handleTripSubmit} open />
      )}

      {openType === "route" && (
        <RoutePortalModal onClose={closeModal} open />
      )}

      {openType === "license" && <LicensePortalModal onClose={closeModal} open />}

      {openType === "expense" && <ExpensePortalModal onClose={closeModal} open />}

      {openType === "dealer" && <DealerPortalModal onClose={closeModal} open />}

      {openType === "subAdmin" && <SubAdminPortalModal onClose={closeModal} open />}

      {openType === "user" && <UserPortalModal onClose={closeModal} open />}

      {openType === "orgStaff" && <EmployeePortalModal onClose={closeModal} open />}

      {openType === "devices" && <DeviceListPortalModal onClose={closeModal} open />}

      {openType === "driver" && <DriverPortalModal onClose={closeModal} open />}

      {openType === "viewer" && <DriverPortalModal onClose={closeModal} open entityType="viewer" />}

      {openType === "document" && (
        <AddDocumentModal onClose={closeModal} onSubmit={handleDocumentSubmit} open />
      )}

      {openType === "material" && (
        <AddMaterialModal onClose={closeModal} onSubmit={handleMaterialSubmit} open />
      )}

      {openType === "client" && (
        <AddClientModal onClose={closeModal} onSubmit={handleClientSubmit} open />
      )}

      {openType === "sensor" && (
        <SensorPortalModal onClose={closeModal} open />
      )}

      {openType === "deviceModel" && (
        <DeviceModelModal onClose={closeModal} open />
      )}

      {openType === "reminder" && <ReminderPortalModal onClose={closeModal} open />}

      {openType === "report" && (
        <ReportsPortal
          isOpen
          onClose={closeModal}
          allDevices={payload?.allDevices || []}
          rawData={payload?.rawData || null}
        />
      )}
    </>
  );
}
